import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  list,
  add,
  update,
  remove,
} from "../../services/attractionMasterService";
import Dialog from "../dialog/Dialog";
import FilePreview from "../FilePreview/FilePreview";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Toster from "../common/Toster";
import Pagination from "../common/Pagination";
import { buildPaginationMeta } from "../../utils/clientPagination";
import ImageInput from "../common/ImageInput";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../common/supplierPageMeta";
import { getTableEmptyProps } from "../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierFilterToolbar,
  SupplierFilterField,
} from "../common/supplierPageUi";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../common/adminTable";

const PackageAttractions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isActiveLoading, setIsActiveLoading] = useState(false);

  const [attractionName, setAttractionName] = useState("");
  const [attractionStatus, setAttractionStatus] = useState(1);
  const [attractionImage, setAttractionImage] = useState(null);
  const [errors, setErrors] = useState({});

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["attractionsMaster", currentPage],
    queryFn: () => list(currentPage),
  });

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage("");
    }, 3000);
  };

  const addMutation = useMutation({
    mutationFn: add,
    onSuccess: (success) => {
      queryClient.invalidateQueries(["attractionsMaster"]);
      setAddDialogOpen(false);
      clearFormData();
      setIsActiveLoading(false);
      showSuccessMessage(success?.message || "Attraction added successfully");
    },
    onError: (error) => {
      setIsActiveLoading(false);
      showErrorMessage(error.response?.data?.message || "Something went wrong");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => update(id, formData),
    onSuccess: (success) => {
      queryClient.invalidateQueries(["attractionsMaster"]);
      setEditDialogOpen(false);
      clearFormData();
      setSelectedAttraction(null);
      setIsActiveLoading(false);
      showSuccessMessage(success?.message || "Attraction updated successfully");
    },
    onError: (error) => {
      setIsActiveLoading(false);
      showErrorMessage(error.response?.data?.message || "Something went wrong");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: remove,
    onSuccess: (success) => {
      queryClient.invalidateQueries(["attractionsMaster"]);
      setDeleteDialogOpen(false);
      setSelectedAttraction(null);
      setIsActiveLoading(false);
      showSuccessMessage(success?.message || "Attraction deleted successfully");
    },
    onError: (error) => {
      setIsActiveLoading(false);
      showErrorMessage(error.response?.data?.message || "Something went wrong");
    },
  });

  const clearFormData = () => {
    setAttractionName("");
    setAttractionStatus(1);
    setAttractionImage(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!attractionName)
      newErrors.attractionName = "Attraction name is required";
    if (!attractionImage && !selectedAttraction)
      newErrors.attractionImage = "Attraction image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddClick = () => {
    clearFormData();
    setAddDialogOpen(true);
  };

  const handleEditClick = (id) => {
    const attraction = data.data.data.find((a) => a.id === id);
    setSelectedAttraction(attraction);
    setAttractionName(attraction.name || "");
    setAttractionStatus(attraction.status ?? 1);
    setAttractionImage(null);
    setErrors({});
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (attraction) => {
    setSelectedAttraction(attraction);
    setDeleteDialogOpen(true);
  };

  const handleSaveAttraction = async () => {
    setIsActiveLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", attractionName);
      formData.append("status", attractionStatus);

      if (attractionImage instanceof File) {
        formData.append("image", attractionImage);
      }

      addMutation.mutate(formData, {
        onSuccess: () => {
          setAddDialogOpen(false);
          clearFormData();
        },
      });
    } else {
      setIsActiveLoading(false);
    }
  };

  const handleUpdateAttraction = async () => {
    setIsActiveLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", attractionName);
      formData.append("status", attractionStatus);

      if (attractionImage instanceof File) {
        formData.append("image", attractionImage);
      }

      updateMutation.mutate(
        { id: selectedAttraction.id, formData },
        {
          onSuccess: (success) => {
            setEditDialogOpen(false);
            clearFormData();
            setSelectedAttraction(null);
            setIsActiveLoading(false);
            showSuccessMessage(
              success.response?.data?.message ||
                "Attraction updated successfully"
            );
          },
          onError: (error) => {
            setIsActiveLoading(false);
            showErrorMessage(
              error.response?.data?.message || "Something went wrong"
            );
          },
        }
      );
    } else {
      setIsActiveLoading(false);
    }
  };

  const handleDeleteAttraction = () => {
    setIsActiveLoading(true);
    deleteMutation.mutate(selectedAttraction.id);
  };

  const pageMeta = SUPPLIER_PAGE_META.masterAttractions;

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="p-6 text-gray-500">Loading...</div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="p-6 text-red-600">Error: {error.message}</div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (!data?.data?.data) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="p-6 text-gray-500">No attractions found</div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  const attractions = data.data.data;
  const { current_page, last_page, total, per_page = 10 } = data.data;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={
          <SupplierAddButton onClick={handleAddClick}>Add Attraction</SupplierAddButton>
        }
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar>
            <SupplierFilterField label="Search">
              <TableSearchInput
                placeholder="Search attractions..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </AdminTableFooter>
          }
        >
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Images</AdminTableTh>
            <AdminTableTh>Attractions</AdminTableTh>
            <AdminTableTh>Stage</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {attractions.length > 0 ? (
              attractions.map((attraction, index) => (
                <AdminTableRow key={attraction.id}>
                  <AdminTableCell>
                    {(current_page - 1) * 10 + index + 1}
                  </AdminTableCell>
                  <AdminTableCell>
                    <img
                      src={attraction.image_url}
                      alt="Attraction preview"
                      className="h-8 w-8 object-cover rounded-md"
                    />
                  </AdminTableCell>
                  <AdminTableCell>{attraction.name}</AdminTableCell>
                  <AdminTableCell>
                    <StatusBadge variant={getStatusVariant(attraction.status)}>
                      {attraction.status === 1 ? "Active" : "Inactive"}
                    </StatusBadge>
                  </AdminTableCell>
                  <TableActionsCell>
                    <EditActionButton
                      showLabel={false}
                      onClick={() => handleEditClick(attraction.id)}
                    />
                    <DeleteActionButton
                      showLabel={false}
                      onClick={() => handleDeleteClick(attraction)}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={5}
                {...getTableEmptyProps("packageAttractions", { onAdd: handleAddClick })}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          clearFormData();
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-add"></i>
            Create Attraction
          </div>
        }
        primaryButtonText="Save"
        primaryButtonAction={handleSaveAttraction}
        closeButtonText="Close"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          clearFormData();
        }}
        isLoading={isActiveLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attraction <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-2 py-1 border ${
                errors.attractionName ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter attraction name"
              value={attractionName}
              onChange={(e) => setAttractionName(e.target.value)}
            />
            {errors.attractionName && (
              <p className="text-red-500 text-xs mt-1">
                {errors.attractionName}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              className="placeholder:text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
              value={attractionStatus}
              onChange={(e) => setAttractionStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div>
            <ImageInput
              onChange={setAttractionImage}
              error={errors.attractionImage}
              label="Attraction Image"
              required={true}
              className=""
              showRemoveForExisting={false}
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {attractionImage && (
            <FilePreview
              file={attractionImage}
              fileUrl={URL.createObjectURL(attractionImage)}
              label="Attraction Image"
              onDelete={() => setAttractionImage(null)}
              isImage={true}
            />
          )}
        </div>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          clearFormData();
          setSelectedAttraction(null);
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Update Attraction
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateAttraction}
        closeButtonText="Close"
        closeButtonAction={() => {
          setEditDialogOpen(false);
          clearFormData();
          setSelectedAttraction(null);
        }}
        isLoading={isActiveLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attraction <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`placeholder:text-sm w-full px-3 py-1 border ${
                errors.attractionName ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter attraction name"
              value={attractionName}
              onChange={(e) => setAttractionName(e.target.value)}
            />
            {errors.attractionName && (
              <p className="text-red-500 text-xs mt-1">
                {errors.attractionName}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              className="placeholder:text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
              value={attractionStatus}
              onChange={(e) => setAttractionStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div>
            <ImageInput
              onChange={setAttractionImage}
              error={errors.attractionImage}
              label=" Attraction Image"
              required={false}
              className=""
              existingImage={selectedAttraction?.image_url}
              showRemoveForExisting={true}
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {attractionImage ? (
            <FilePreview
              file={attractionImage}
              width="w-[100px]"
              height="h-[100px]"
              label="New Image"
              onDelete={() => setAttractionImage(null)}
              isImage={true}
            />
          ) : selectedAttraction && selectedAttraction.image_url ? (
            <FilePreview
              file={selectedAttraction.image_url}
              width="w-[100px]"
              height="h-[100px]"
              label="Current Image"
              isImage={true}
              showDelete={true}
            />
          ) : (
            <div className="text-sm text-gray-500">No image selected</div>
          )}
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Attraction
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteAttraction}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isActiveLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this attraction? This action cannot
            be undone.
          </p>
        </div>
      </Dialog>

      {successMessage && (
        <Toster
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <Toster
          message={errorMessage}
          type="error"
          onClose={() => setErrorMessage("")}
        />
      )}
    </AdminPageLayout>
  );
};

export default PackageAttractions;
